import React, { useEffect, useState } from 'react';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';

import TableTree from '@atlaskit/table-tree/table-tree';
import { Cell } from '@atlaskit/table-tree/cell';
import { Header } from '@atlaskit/table-tree/header';
import { Headers } from '@atlaskit/table-tree/headers';
import { Row } from '@atlaskit/table-tree/row';
import { Rows } from '@atlaskit/table-tree/rows';

import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';

import IconButton from '@atlaskit/button/icon/button';
import Popup from '@atlaskit/popup';
import Lozenge, { type ThemeAppearance } from '@atlaskit/lozenge/lozenge';
import ButtonItem from '@atlaskit/menu/button-item';
import LinkItem from '@atlaskit/menu/link-item';
import Section from '@atlaskit/menu/section';
import Spinner from '@atlaskit/spinner/spinner';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';

import { useIntl, defineMessage } from '@atlassian/jira-intl';
import type {
	ApplicationLinkV3Data,
	ApplicationLinkV3Status,
} from './CommonApplicationLinkCreationEntity.tsx';
import { deleteApplink, fetchApplinkStatus } from './fetchApplinkManifestForUrl.tsx';
import {
	APPLINKS_EDIT_PAGE_URL_FOR_ID,
	APPLINKS_LIST_PAGE_URL,
} from './applinksListPageUrl.constants.tsx';
import { lookupTypeFromV3Data } from './TypeDefinition.types.tsx';
import ErrorStatusSectionMessage from './ErrorStatusSectionMessage.tsx';
import { ERRORS } from './Errors.tsx';

const primary = defineMessage({
	id: 'applinks.administration.list.applinks-table.primary',
	defaultMessage: 'Primary',
	description:
		'The text is shown as a lozenge badge label when an application link is marked as the primary link. Appears next to the application link type to indicate its primary status.',
});

const extraAuth = defineMessage({
	id: 'applinks.administration.list.applinks-table.extra-auth',
	defaultMessage: 'Extra Auth',
	description:
		'The text is shown as a lozenge badge label when an application link requires extra authentication. Appears next to the application link type to indicate additional authentication is needed.',
});

const connectedStatus = defineMessage({
	id: 'applinks.administration.ui.common.status.connected-status',
	defaultMessage: 'Connected',
	description:
		'The text is shown as a status lozenge label when an application link is successfully connected and working. Appears in the status column of the application links table.',
});

const authTypeUnknown = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-unknown',
	defaultMessage: 'Unknown',
	description:
		'The text is shown as a status lozenge label when the authentication type of an application link cannot be determined. Appears in the status column of the application links table.',
});

const remote = defineMessage({
	id: 'applinks.administration.list.applinks-table.remote',
	defaultMessage: 'Go to remote',
	description:
		'The text is shown as a menu item label when the user wants to navigate to the remote application link. Used in the actions dropdown menu to open the linked application.',
});

const edit = defineMessage({
	id: 'applinks.administration.list.applinks-table.edit',
	defaultMessage: 'Edit',
	description:
		'The text is shown as a menu item label when the user wants to edit an application link. Used in the actions dropdown menu to open the edit page for the selected application link.',
});

const deleteLink = defineMessage({
	id: 'applinks.administration.list.applinks-table.delete-link',
	defaultMessage: 'Delete',
	description:
		'The text is shown as a menu item label when the user wants to delete an application link. Used in the actions dropdown menu to remove the selected application link.',
});

const name = defineMessage({
	id: 'applinks.administration.list.applinks-table.name',
	defaultMessage: 'Name',
	description:
		'The text is shown as a table column header for the application link name. Appears in the table header row to label the name column.',
});

const application = defineMessage({
	id: 'applinks.administration.list.applinks-table.application',
	defaultMessage: 'Application',
	description:
		'The text is shown as a table column header for the application link details. Appears in the table header row to label the application column.',
});

const linkProperties = defineMessage({
	id: 'applinks.administration.list.applinks-table.link-properties',
	defaultMessage: 'Link properties',
	description:
		'The text is shown as a table column header for application link properties such as primary status and extra auth requirements. Appears in the table header row to label the link properties column.',
});

const status = defineMessage({
	id: 'applinks.administration.list.applinks-table.status',
	defaultMessage: 'Status',
	description:
		'The text is shown as a table column header for the application link connection status. Appears in the table header row to label the status column.',
});

const actions = defineMessage({
	id: 'applinks.administration.list.applinks-table.actions',
	defaultMessage: 'Actions',
	description:
		'The text is shown as a table column header for application link action buttons. Appears in the table header row to label the actions column.',
});

const systemApplinkNonfinal = defineMessage({
	defaultMessage:
		'This is an internal link created automatically by the system. System internal links are read-only and cannot be edited.',
	id: 'applinks.administration.ui.common.status.system-link-description',
	description:
		'Description text displayed in a status popup when hovering over a system application link status lozenge. Explains that system links are automatically created and cannot be edited by users.',
});

const contentStyles = cssMap({
	status: {
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		maxWidth: '500px',
		minWidth: '320px',
	},
	menu: {
		maxWidth: '500px',
		minWidth: '320px',
	},
});

const ApplicationLinkProduct = (props: ApplicationLinkV3Data) => {
	const typeDef = lookupTypeFromV3Data(props);

	return (
		<>
			<Inline alignBlock="center" space="space.100">
				{typeDef.icon}
				<Stack>
					<Text>{props.displayUrl}</Text>
					<Text color="color.text.subtle" size="small">
						{typeDef.name}
					</Text>
				</Stack>
			</Inline>
		</>
	);
};

const ApplicationLinkName = (props: ApplicationLinkV3Data) => {
	return <Text>{props.name}</Text>;
};

const ApplicationLinkType = (props: ApplicationLinkV3Data) => {
	const { formatMessage } = useIntl();
	const appendLozenges = (isPrimary: boolean, isSystem: boolean, isExtraAuthRequired: boolean) => {
		return (
			<>
				{isPrimary && <Lozenge key="primary">{formatMessage(primary)}</Lozenge>}
				{isExtraAuthRequired && <Lozenge key="extra-auth">{formatMessage(extraAuth)}</Lozenge>}
			</>
		);
	};

	return (
		<Inline alignBlock="center" space="space.100">
			{appendLozenges(props.primary, props.system, props.isExtraAuthRequired)}
		</Inline>
	);
};

const LozengeWithContext = (props: {
	appearance: ThemeAppearance;
	label: string;
	content: React.ReactNode;
}) => {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<Inline alignBlock="center">
			<Lozenge appearance={props.appearance}>{props.label}</Lozenge>
			<Popup
				shouldRenderToParent
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				placement="bottom-start"
				content={() => <Box xcss={contentStyles.status}>{props.content}</Box>}
				trigger={(triggerProps) => (
					<IconButton
						{...triggerProps}
						icon={StatusInformationIcon}
						label={`Additional information about ${props.label} status`}
						appearance="subtle"
						onClick={() => setIsOpen(!isOpen)}
					/>
				)}
			/>
		</Inline>
	);
};

const ApplicationLinkStatus = (props: ApplicationLinkV3Data) => {
	const { formatMessage } = useIntl();
	const [applinkStatus, setApplinkStatus] = useState<ApplicationLinkV3Status | 'system' | null>(
		null,
	);

	useEffect(() => {
		// If the applink is a system link, we don't need to fetch its status
		if (props.system) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
			setApplinkStatus('system');
			return;
		}

		// TODO: Properly mock data
		fetchApplinkStatus(props.id)
			.then((linkStatus: ApplicationLinkV3Status) => {
				setApplinkStatus(linkStatus);
			})
			.catch((err) => {
				const maybeError = err instanceof Error ? err : undefined;
				log.safeErrorWithoutCustomerData(
					'applinks.administration.list.fetchApplinkStatus',
					`Failed to fetch applink status for the table row belonging to applink ${props.id}`,
					maybeError,
				);
			});
	}, [props.id, props.system]);

	// TODO: Pop-ups
	const createLozenge = (contents: ApplicationLinkV3Status | 'system') => {
		if (contents === 'system') {
			return (
				<LozengeWithContext
					appearance="default"
					label="System"
					content={
						<Stack space="space.100">
							{/* TODO: Try reuse ErrorStatusSectionMessage here */}
							<Text>{formatMessage(systemApplinkNonfinal)}</Text>
						</Stack>
					}
				/>
			);
		}

		// First, check if the link is working
		if (contents.working) {
			return <Lozenge appearance="success">{formatMessage(connectedStatus)}</Lozenge>;
		}

		// Next, check if there is an error
		if (contents.error) {
			return (
				<LozengeWithContext
					appearance="removed"
					label="Config Error"
					content={
						<Stack space="space.100">
							{/* TODO: Don't use a section message, rather add a proper pop-up control */}
							<ErrorStatusSectionMessage
								err={contents.error ? ERRORS[contents.error.type] : ERRORS.UNKNOWN}
								context={contents}
							/>
						</Stack>
					}
				/>
			);
		}

		// TODO: Others
		return <Lozenge>{formatMessage(authTypeUnknown)}</Lozenge>;
	};

	return applinkStatus ? createLozenge(applinkStatus) : <Spinner />;
};

const onDeleteButtonClick = (applink: ApplicationLinkV3Data) => {
	deleteApplink(applink.id);
};

const ApplicationLinkPopupMenuContent = (props: { data: ApplicationLinkV3Data }) => {
	const { formatMessage } = useIntl();
	return (
		<Box>
			<Stack xcss={contentStyles.menu}>
				<Section>
					<LinkItem href={props.data.displayUrl + APPLINKS_LIST_PAGE_URL}>
						{formatMessage(remote)}
					</LinkItem>
				</Section>
				<Section title="Manage link" hasSeparator>
					<LinkItem href={APPLINKS_EDIT_PAGE_URL_FOR_ID(props.data.id)}>
						{formatMessage(edit)}
					</LinkItem>
					<ButtonItem onClick={() => onDeleteButtonClick(props.data)}>
						{formatMessage(deleteLink)}
					</ButtonItem>
				</Section>
			</Stack>
		</Box>
	);
};

const ApplicationLinkPopupMenu = (props: { data: ApplicationLinkV3Data }) => {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			content={() => <ApplicationLinkPopupMenuContent data={props.data} />}
			placement="bottom-start"
			shouldRenderToParent
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					isSelected={isOpen}
					label="Additional actions"
					onClick={() => {
						setIsOpen(!isOpen);
					}}
					icon={ShowMoreHorizontalIcon}
				/>
			)}
		/>
	);
};

type Item = {
	id: string;
	content: ApplicationLinkV3Data;
	hasChildren: boolean;
	children?: Item[];
};

const convertToArray = (applinks: ApplicationLinkV3Data[]): Item[] => {
	const wrapWithItem = (
		id: string,
		content: ApplicationLinkV3Data,
		hasChildren: boolean,
		children?: Item[],
	) => {
		return { id, content, hasChildren, children };
	};

	return applinks.map((entry, index) => {
		return wrapWithItem(`entry-${index}`, entry, false);
	});
};

const ApplinksTable = (props: {
	search: string;
	filter: string | null;
	applinks: ApplicationLinkV3Data[] | null;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const applinkItems = props.applinks ? convertToArray(props.applinks) : undefined;

	const filteredApplinkItems = applinkItems?.filter((item) => {
		let passesFilter = true;
		if (props.filter) {
			passesFilter = item.content.type === props.filter;
		}

		let passesSearch = true;
		if (props.search && props.search.length > 0) {
			const nameCondition = item.content.name.toLowerCase().includes(props.search.toLowerCase());
			const urlCondition = item.content.displayUrl
				.toLowerCase()
				.includes(props.search.toLowerCase());
			passesSearch = nameCondition || urlCondition;
		}
		return passesFilter && passesSearch;
	});

	return (
		<TableTree label="Application Links">
			<Headers>
				<Header width="20%">{formatMessage(name)}</Header>
				<Header width="40%">{formatMessage(application)}</Header>
				<Header width="15%">{formatMessage(linkProperties)}</Header>
				<Header width="15%">{formatMessage(status)}</Header>
				<Header width="10%">{formatMessage(actions)}</Header>
			</Headers>
			<Rows
				items={filteredApplinkItems}
				render={(item: Item) => {
					const { content } = item;

					const row = (
						<Row key={item.id} itemId={item.id} hasChildren={item.hasChildren}>
							<Cell>
								<ApplicationLinkName {...content} />
							</Cell>
							<Cell>
								<ApplicationLinkProduct {...content} />
							</Cell>
							<Cell>
								<ApplicationLinkType {...content} />
							</Cell>
							<Cell>
								<ApplicationLinkStatus {...content} />
							</Cell>
							<Cell>
								<ApplicationLinkPopupMenu data={item.content} />
							</Cell>
						</Row>
					);

					return row;
				}}
			/>
		</TableTree>
	);
};

export default ApplinksTable;
